import React, { FC } from 'react';
import {
  CrewDayPlan,
  CrewDayPlanEvent,
  CrewEventVenue,
  CrewGroupEvent,
} from '../../../shared/types/crewSchedule';
import { Time } from '../../../shared/types/time';
import { timeToMinutes, minutesToTimeString } from '@go-shared/lib/time';
import { capitalize } from '../../../shared/lib/string';
import { HourFormat } from '../../../shared/types/brand';
import { InlineIcon } from '@spark-labs/avocado-ui';

export interface CrewDayProgramProps {
  plan?: CrewDayPlan;
}

// Something that has an event shape, mostly startTime, duration, title and hidden
interface EventLike {
  title: string;
  startTime: Time;
  duration: number;
  hidden?: boolean;
  venue: CrewEventVenue;
  groupEvent: boolean;
}

// -- Mostly copied from the CrewDayschedule
function getStartTime(event: EventLike, hourFormat: HourFormat): string {
  let eventStart = timeToMinutes(event.startTime);

  return minutesToTimeString(eventStart, hourFormat, 'compact');
}

function getEndTime(event: EventLike, hourFormat: HourFormat): string {
  let eventStart = timeToMinutes(event.startTime);

  return minutesToTimeString(eventStart + event.duration, hourFormat, 'compact');
}

function getVenue(event: EventLike): string {
  let location = event.venue.location ? capitalize(event.venue.location) : '';
  return `${event.venue.name}, Deck ${event.venue.level} ${location}`;
}

type CrewEventSorter = (e1: EventLike, e2: EventLike) => number;

function makeDayProgramSorter(dayStart: Time): CrewEventSorter {
  let dayStartMinutes = timeToMinutes(dayStart);
  return function sortCrewEvents(e1: EventLike, e2: EventLike): number {
    let startTime1 = timeToMinutes(e1.startTime, dayStartMinutes);
    let startTime2 = timeToMinutes(e2.startTime, dayStartMinutes);

    let duration1 = e1.duration;
    let duration2 = e2.duration;

    if (startTime1 !== startTime2) return startTime1 - startTime2;
    if (duration1 !== duration2) return duration1 - duration2;

    return e1.title.localeCompare(e2.title);
  };
}

function crewEventToEventLike(event: CrewDayPlanEvent): EventLike {
  return {
    title: event.title,
    startTime: event.startTime,

    duration: event.duration,
    hidden: event.hidden,
    venue: event.venue,
    groupEvent: false,
  };
}

function crewGroupEventToEventLike(groupEvent: CrewGroupEvent): EventLike {
  return {
    title: groupEvent.title,
    startTime: groupEvent.startTime,

    duration: groupEvent.duration,
    venue: groupEvent.venue,
    groupEvent: true,
  };
}

// Given a list of events and a list of groupEvents, join them as `EventLike` objects, which have
// the required information to be displayed in the app
function joinEvents(events: CrewDayPlanEvent[], groupEvents: CrewGroupEvent[]): EventLike[] {
  return events.map(crewEventToEventLike).concat(groupEvents.map(crewGroupEventToEventLike));
}

function getEventIcon(event: EventLike): JSX.Element | undefined {
  if (event.groupEvent) {
    return <InlineIcon className="w-3 h-3" name={'group'} />;
  }

  if (event.hidden) {
    return <InlineIcon className="w-3 h-3" name={'no-eye'} />;
  }
}

const CrewDayProgram: FC<CrewDayProgramProps> = ({ plan }) => {
  if (!plan) return <div />;

  let joined = joinEvents(plan.events, plan.groupEvents);
  let crewEventSorter = makeDayProgramSorter(plan.dayStart);

  let events = joined.sort(crewEventSorter);

  return (
    <>
      <h5 className="mt-3 mb-2 ml-2">{plan.voyage}</h5>
      <table className="w-full">
        <tbody>
          {events.map((event, i) => (
            <tr
              key={i}
              className={`text-xs text-grey-darkest border-b border-white bg-grey-lighter`}
            >
              <td className="pl-2 py-1 whitespace-no-wrap">
                {getStartTime(event, plan.hourFormat)}
              </td>
              <td>-</td>
              <td className="pl-1 py-1 whitespace-no-wrap">{getEndTime(event, plan.hourFormat)}</td>
              <td className="py-1">{getEventIcon(event)}</td>
              <td className="pl-1 pr-2 py-1">
                <div>
                  <span className="font-bold">{event.title}</span>, {getVenue(event)}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

export default CrewDayProgram;
